/**
 * Shared report utilities and structure computation.
 * Used by both /chart/report and /chart/report/print to ensure content parity.
 */

export const PLANET_LABELS = {
  sun: 'Sun', moon: 'Moon', mercury: 'Mercury', venus: 'Venus', mars: 'Mars',
  jupiter: 'Jupiter', saturn: 'Saturn', uranus: 'Uranus', neptune: 'Neptune',
  pluto: 'Pluto', northnode: 'North Node', lilith: 'Lilith', chiron: 'Chiron',
};

export const SIGN_ORDER = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

export const ASPECT_LABELS = { conjunction: 'Conjunction', opposition: 'Opposition', trine: 'Trine', square: 'Square', sextile: 'Sextile' };

export const ASPECT_HINTS = { conjunction: 'merged focus', opposition: 'polarity', trine: 'flow', square: 'friction', sextile: 'supportive opening' };

export const HOUSE_MEANINGS = {
  1: 'Identity and first approach', 2: 'Resources and self-worth', 3: 'Learning and communication',
  4: 'Roots and foundations', 5: 'Creativity and expression', 6: 'Routines and craft',
  7: 'Relationships and mirrors', 8: 'Depth and shared bonds', 9: 'Belief and perspective',
  10: 'Direction and contribution', 11: 'Community and future', 12: 'Retreat and integration',
};

const SIGN_THEMES = {
  Aries: 'initiation, assertion, and direct action',
  Taurus: 'steadiness, embodiment, and material comfort',
  Gemini: 'communication, curiosity, and mental flexibility',
  Cancer: 'emotional protection, feeling, and belonging',
  Leo: 'creative visibility, heart-led expression, and pride',
  Virgo: 'precision, craft, and practical refinement',
  Libra: 'relationship, balance, and relational harmony',
  Scorpio: 'depth, intensity, and psychological honesty',
  Sagittarius: 'meaning-seeking, expansion, and perspective',
  Capricorn: 'structure, responsibility, and long-term building',
  Aquarius: 'independence, perspective-shifting, and community',
  Pisces: 'imagination, permeability, and symbolic sensitivity',
};

const HOUSE_THEMES = {
  1: 'identity and first approach', 2: 'resources and self-worth', 3: 'communication and local patterning',
  4: 'roots and emotional foundation', 5: 'creativity and self-expression', 6: 'routines and craft',
  7: 'partnership and relational mirrors', 8: 'shared bonds and depth', 9: 'belief and worldview',
  10: 'direction and public contribution', 11: 'community and future aims', 12: 'retreat and private processing',
};

const AXIS_THEMES = {
  'Aries-Libra': 'assertion versus balance',
  'Taurus-Scorpio': 'security versus depth',
  'Gemini-Sagittarius': 'local exchange versus broader meaning',
  'Cancer-Capricorn': 'protection versus structure',
  'Leo-Aquarius': 'visible expression versus collective perspective',
  'Virgo-Pisces': 'precision versus permeability',
};

export function ordinalHouse(value) {
  const h = Number(value);
  if (h === 1) return '1st';
  if (h === 2) return '2nd';
  if (h === 3) return '3rd';
  return `${h}th`;
}

export function houseTheme(house) {
  const map = {
    1: 'identity, body, and first response', 2: 'resources, value, and security',
    3: 'language, learning, and local patterning', 4: 'home, roots, and emotional base',
    5: 'creativity, pleasure, and self-expression', 6: 'work rhythms, service, and health routines',
    7: 'partnerships, contracts, and direct mirrors', 8: 'shared trust, intimacy, and transformation',
    9: 'belief, study, and worldview', 10: 'direction, public role, and vocation',
    11: 'community, friendships, and future aims', 12: 'retreat, integration, and inner processing',
  };
  return map[Number(house)] || 'the key life themes for this sector';
}

const HOUSE_PATTERNS = {
  1: 'how you meet the world and how the world meets you',
  2: 'what you value and how you build security',
  3: 'how you learn, communicate, and move through local life',
  4: 'where you root, retreat, and find emotional safety',
  5: 'how you create, perform, and take creative risk',
  6: 'how you work, serve, and refine daily craft',
  7: 'how you partner, negotiate, and see yourself in others',
  8: 'how you share depth, trust, and transform through intimacy',
  9: 'how you seek meaning, study, and expand perspective',
  10: 'how you contribute publicly and claim direction',
  11: 'how you belong to groups and orient toward the future',
  12: 'how you process privately and integrate experience',
};

export function generateHouseInterpretation(house, planetsInHouse) {
  const h = Number(house.house);
  const theme = houseTheme(h);
  const sign = house.sign || 'the cusp sign';
  const tone = signTone(sign);
  const pattern = HOUSE_PATTERNS[h] || 'key life themes for this sector';
  const ordinal = ordinalHouse(h);
  let text = `The ${ordinal} house describes ${theme}. With ${sign} on the cusp, this area tends to move through ${tone}. The pattern that often emerges here is ${pattern}.`;
  if (planetsInHouse?.length > 0) {
    const list = planetsInHouse.map((p) => `${p.name || p.key} in ${p.sign}`).join(', ');
    text += ` Active in this house: ${list}. Each planet brings its function into this arena, colored by its sign.`;
  }
  text += ` Notice what repeats in this life area — repetition usually reveals the teaching.`;
  return text;
}

export function signTone(sign) {
  const map = {
    Aries: 'direct, initiating expression', Taurus: 'steadiness, embodiment, material comfort, or resistance to disruption',
    Gemini: 'curious, adaptable expression', Cancer: 'feeling, protection, and emotional memory',
    Leo: 'visible, heart-led expression', Virgo: 'precise, refining expression',
    Libra: 'relational, harmonizing expression', Scorpio: 'intense, depth-seeking expression',
    Sagittarius: 'expansive, meaning-seeking expression', Capricorn: 'structured, pragmatic expression',
    Aquarius: 'independent, perspective-shifting expression', Pisces: 'porous, symbolic expression',
  };
  return map[sign] || `${sign} tone`;
}

export function computeReportStructure(planets) {
  if (!planets?.length) return { signEmphasis: null, houseEmphasis: null, polarityAxes: [], summary: '', toneParagraph: '', coreThemes: [], synthesisThemes: [] };
  const sun = planets.find((p) => p.key === 'sun');
  const moon = planets.find((p) => p.key === 'moon');
  const signCounts = {};
  const houseCounts = {};
  const housePlanets = {};
  planets.forEach((p) => {
    if (p.sign) signCounts[p.sign] = (signCounts[p.sign] || 0) + 1;
    const h = Number(p.house);
    if (Number.isFinite(h)) {
      houseCounts[h] = (houseCounts[h] || 0) + 1;
      housePlanets[h] = housePlanets[h] || [];
      housePlanets[h].push(PLANET_LABELS[String(p.key).toLowerCase()] || p.key);
    }
  });
  const signEmphasis = Object.entries(signCounts).filter(([, c]) => c >= 3).sort((a, b) => b[1] - a[1])[0];
  const houseEmphasis = Object.entries(houseCounts).filter(([, c]) => c >= 3).sort((a, b) => b[1] - a[1])[0];
  const polarityAxes = [];
  for (let i = 0; i < 6; i++) {
    const a = SIGN_ORDER[i];
    const b = SIGN_ORDER[i + 6];
    if ((signCounts[a] || 0) > 0 && (signCounts[b] || 0) > 0) polarityAxes.push({ signA: a, signB: b });
  }
  const parts = [];
  if (signEmphasis) parts.push(`${signEmphasis[0]} appears ${signEmphasis[1]} times`);
  if (houseEmphasis) parts.push(`the ${ordinalHouse(houseEmphasis[0])} house holds ${houseEmphasis[1]} placements`);
  if (polarityAxes.length) parts.push(`${polarityAxes.length} polarity axis${polarityAxes.length > 1 ? 'es' : ''} active`);
  const summary = parts.length ? `This chart concentrates energy where ${parts.join(', and ')}.` : '';

  const toneParts = [];
  if (signEmphasis) toneParts.push(`A strong emphasis on ${signEmphasis[0]} suggests themes of ${SIGN_THEMES[signEmphasis[0]] || 'that sign\'s expression'}.`);
  if (houseEmphasis) toneParts.push(`The ${ordinalHouse(houseEmphasis[0])} house emphasis points toward ${HOUSE_THEMES[houseEmphasis[0]] || 'that life arena'}.`);
  if (polarityAxes.length > 0) {
    const axis = polarityAxes[0];
    const key = `${axis.signA}-${axis.signB}`;
    toneParts.push(`The ${axis.signA}–${axis.signB} axis suggests a dynamic between ${AXIS_THEMES[key] || 'these two poles'}.`);
  }
  const toneParagraph = toneParts.length ? toneParts.join(' ') : 'The chart\'s structure reveals where energy concentrates and how themes repeat.';

  const coreThemes = [];
  if (signEmphasis) coreThemes.push({ label: `${signEmphasis[0]} emphasis`, theme: SIGN_THEMES[signEmphasis[0]] || 'repeated expression through this sign' });
  if (houseEmphasis) coreThemes.push({ label: `${ordinalHouse(houseEmphasis[0])} House`, theme: HOUSE_THEMES[houseEmphasis[0]] || 'concentration in this life area' });
  polarityAxes.slice(0, 2).forEach(({ signA, signB }) => {
    const key = `${signA}-${signB}`;
    coreThemes.push({ label: `${signA}–${signB} axis`, theme: AXIS_THEMES[key] || 'tension or dialogue between these poles' });
  });
  if (coreThemes.length < 3 && sun) coreThemes.push({ label: 'Sun in ' + sun.sign, theme: 'core identity and creative will' });
  if (coreThemes.length < 4 && moon) coreThemes.push({ label: 'Moon in ' + moon.sign, theme: 'emotional needs and instinctive response' });

  const synthesisThemes = [];
  const cardinalCount = planets.filter((p) => ['Aries', 'Cancer', 'Libra', 'Capricorn'].includes(p.sign)).length;
  const fixedCount = planets.filter((p) => ['Taurus', 'Leo', 'Scorpio', 'Aquarius'].includes(p.sign)).length;
  const mutableCount = planets.filter((p) => ['Gemini', 'Virgo', 'Sagittarius', 'Pisces'].includes(p.sign)).length;
  if (cardinalCount >= 4) synthesisThemes.push('strong cardinal emphasis — initiation and action');
  if (fixedCount >= 4) synthesisThemes.push('strong fixed emphasis — persistence and stability');
  if (mutableCount >= 4) synthesisThemes.push('strong mutable emphasis — adaptability and change');
  const lowerCount = planets.filter((p) => [1, 2, 3, 4, 5, 6].includes(Number(p.house))).length;
  const upperCount = planets.filter((p) => [7, 8, 9, 10, 11, 12].includes(Number(p.house))).length;
  if (lowerCount >= 6) synthesisThemes.push('concentration in lower hemisphere — personal and inner focus');
  if (upperCount >= 6) synthesisThemes.push('concentration in upper hemisphere — relational and public focus');
  const axis17 = (houseCounts[1] || 0) + (houseCounts[7] || 0);
  if (axis17 >= 3) synthesisThemes.push('strong 1st–7th house axis — identity and partnership in dialogue');
  const venusMoonCount = planets.filter((p) => ['venus', 'moon'].includes(p.key)).length;
  if (venusMoonCount >= 2 && houseEmphasis && [2, 5, 7, 8].includes(Number(houseEmphasis[0]))) {
    synthesisThemes.push('Venus–Moon–relational patterning — values, feeling, and connection');
  }

  return {
    signEmphasis: signEmphasis ? { sign: signEmphasis[0], count: signEmphasis[1] } : null,
    houseEmphasis: houseEmphasis ? { house: Number(houseEmphasis[0]), count: houseEmphasis[1], planets: housePlanets[houseEmphasis[0]] || [] } : null,
    polarityAxes,
    summary,
    toneParagraph,
    coreThemes: coreThemes.slice(0, 5),
    synthesisThemes: synthesisThemes.slice(0, 4),
  };
}
